import {isSignal, OutputRef, Signal} from '@angular/core';
import {outputToObservable, toSignal, ToSignalOptions} from '@angular/core/rxjs-interop';

/**
 * This function is used to extract the innermost value from a signal.
 * It takes a signal as an input and keeps invoking it until it's no longer a signal.
 * The final value is then returned.
 *
 * @template T The expected return type of the innermost signal.
 * @param {any} signal The initial signal to start the extraction from.
 * @returns {T} The innermost value extracted from the signal.
 */
export function innerSignal<T>(signal: any): T {
	let value: any;
	// Keep invoking the signal until it's no longer a signal
	while (isSignal(signal)) {
		value = signal();
		signal = value;
	}
	// Return the innermost value
	return value;
}

/**
 * This function is used to convert an output reference to a signal.
 * It takes an output reference as an input and returns a signal.
 *
 * @template T The type of the output reference.
 * @param {OutputRef<T>} ref The output reference to convert to a signal.
 * @param {ToSignalOptions} [options] The options to use when converting the output reference to a signal.
 * @returns {Signal<T>} The signal that was converted from the output reference.
 */
export function ouputToSignal<T>(ref: OutputRef<T>, options?: ToSignalOptions<any>): Signal<T> {
	const observable$ = outputToObservable(ref);
	return toSignal(observable$);
}
